<template>
  <layout name="Sidebar" extraClass="sidebar-dashboard">
    <div class="dashboard-mail">
      <VSidebarLayoutHeading title="Dashboard" extraClass="dashboard" />

      <WithLoader
        :isLoading="isLoadingInitial"
        extraClass="dashboard-mail-loader-initial"
        classType="primary"
      >
        <div
          class="dashboard-mail__content light"
          :class="{ ['isLoading']: isLoading }"
        >
          <div
            v-if="isLoading"
            class="filters-overlay"
            ref="filtersOverlay"
          ></div>
          <div
            v-show="filtersOpened || templateFiltersOpened || datesRangeOpened"
            class="filters-wrapper"
          >
            <div ref="templatesFilters" class="template-filters-wrapper">
              <VDatePicker
                v-show="filtersOpened || datesRangeOpened"
                mode="range"
                :value="selectedDate"
                :onChange="onDateChange"
                isInline
              ></VDatePicker>

              <div
                class="type-filters"
                v-show="filtersOpened || templateFiltersOpened"
              >
                <VTemplatesFilter
                  :templates="filteredTemplates"
                  :letterTemplates="lettersTemplatesList"
                  :startDate="start_date"
                  :endDate="end_date"
                  :searchString="searchTemplateString"
                  :selectedNames="filteredNames"
                  :selectedPostcardSizes="filteredPostcardSizes"
                  :selectedLetterSizes="filteredLetterSizes"
                  @change="_onChangeFilterTemplate"
                  @search="onSearchTemplate"
                />
              </div>
            </div>
          </div>
          <div class="top-line">
            <div class="stats light">
              <div class="stats__item">
                <div class="stats__item-description">
                  Total Spend
                </div>
                <div class="stats__item-main regular">
                  ${{ _totalCostString }}
                </div>
              </div>
              <div class="stats__item">
                <div class="stats__item-description">
                  Total Volume
                </div>
                <div class="stats__item-main regular">
                  {{
                    total &&
                      total.toLocaleString("en-us", {
                        maximumFractionDigits: 2
                      })
                  }}
                </div>
              </div>
            </div>
            <div class="filters-right">
              <div class="filters">
                <div
                  class="template-filters hide-m"
                  :class="{
                    ['active']: templateFiltersOpened || filtersOpened
                  }"
                  @click="templateFiltersOpened = !templateFiltersOpened"
                >
                  {{ _selectedFilterTypesString }}
                </div>

                <div
                  class="date-range hide-m"
                  @click="datesRangeOpened = !datesRangeOpened"
                  @mouseover="datesRangeHovered = true"
                  @mouseout="datesRangeHovered = false"
                  :class="{ ['active']: datesRangeOpened || filtersOpened }"
                >
                  <span> {{ _formatDate(start_date, true) }} </span>
                  <img
                    :src="
                      datesRangeOpened || datesRangeHovered || filtersOpened
                        ? arrowSmallIconActive
                        : arrowSmallIcon
                    "
                    alt="arrow"
                  />
                  <span> {{ _formatDate(end_date, true) }}</span>
                </div>

                <div
                  class="select-filters"
                  @mouseover="filtersHovered = true"
                  @mouseout="filtersHovered = false"
                  @click="_handleFiltersIconClick"
                >
                  <img ref="filterIconToggle" :src="_filterIcon" alt="filter" />
                </div>
              </div>

              <div class="quick-filters" @click="_quickFilterHandler">
                <span
                  v-for="qf in quickFiltersList"
                  :class="{ ['active']: quickFilter === qf }"
                >
                  {{ qf }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="items.length" class="dashboard-mail__chart-wrapper">
            <div class="checkpoints">
              <div
                class="checkpoint"
                :style="{ 'margin-left': `${_leftCheckpointMargin}px` }"
              >
                <div>{{ _formatDate(start_date) }}</div>
                <div class="dots"></div>
              </div>
              <div class="checkpoint current">
                {{ _formatDate(start_date) }} - {{ _formatDate(end_date) }}
              </div>
              <div class="checkpoint right">
                <div>{{ _formatDate(end_date) }}</div>
                <div class="dots"></div>
              </div>
            </div>

            <WithLoader
              :isLoading="isLoading"
              extraClass="dashboard-mail-loader"
              classType="primary"
            >
              <div class="chart-wrap">
                <VChart
                  :items="items"
                  xAxisName="date"
                  yAxisName="total"
                  @maxchanged="_onMaxValueChange"
                  @minchanged="_onMinValueChange"
                />
              </div>
            </WithLoader>
          </div>
        </div>
      </WithLoader>
    </div>
  </layout>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import debounce from "lodash/debounce";
import mapKeys from "lodash/mapKeys";

import Layout from "@/layouts/Layout";
import WithLoader from "@/components/WithLoader";

import VSidebarLayoutHeading from "@/components/VSidebarLayoutHeading";
import VHr from "@/components/VHr";
import VChart from "@/components/VChart";
import VTemplatesFilter from "@/components/VTemplatesFilter";
import VDatePicker from "@/components/VDatePicker";

import filterIcon from "@/assets/images/filter-new.svg";
import filterIconActive from "@/assets/images/filter-new-active.svg";
import calendarIcon from "@/assets/images/calendar.svg";
import arrowSmallIcon from "@/assets/images/arrow-small.svg";
import arrowSmallIconActive from "@/assets/images/arrow-small-active.svg";

import {
  getLetterType,
  getLetterColor,
  convertMailType,
  getPostcardFullType,
  convertFullTypeKeyToString
} from "@/utils/util";

export default Vue.component("DashboardMail", {
  metaInfo: {
    title: "Dashboard"
  },

  components: {
    VSidebarLayoutHeading,
    Layout,
    VHr,
    VChart,
    WithLoader,
    VTemplatesFilter,
    VDatePicker
  },

  data() {
    return {
      filterIcon,
      calendarIcon,
      arrowSmallIcon,
      arrowSmallIconActive,

      // filter window states
      filtersOpened: false,
      datesRangeOpened: false,
      datesRangeHovered: false,
      templateFiltersOpened: false,
      filtersHovered: false,

      searchTemplateString: "",
      debouncedFetchTemplate: debounce(this.filterTemplates, 300),
      maxChartTotalValue: 0,
      minChartTotalValue: 0,
      quickFiltersList: ["1W", "2W", "1M", "3M", "1Y", "MTD", "QTD", "YTD"]
    };
  },

  methods: {
    _handleFiltersIconClick(e) {
      this.filtersOpened = !this.filtersOpened;
    },

    onSearchTemplate(val) {
      this.debouncedFetchTemplate({ search: val });
      this.searchTemplateString = val;
    },

    _onMaxValueChange(val) {
      this.maxChartTotalValue = val;
    },

    _onMinValueChange(val) {
      this.minChartTotalValue = val;
    },

    _onChangeFilterTemplate(
      type,
      data,
      initial = false,
      templateType = "postcard"
    ) {
      if (!initial) {
        // if (data.length === 0) {
        //   return;
        // }
        const start = this._formatDate(this.start_date, true);
        const end = this._formatDate(this.end_date, true);
        const templateNames = type === "size" ? this.filteredNames : data;

        const postcardTypesSlice = this.filteredSizes.filter(
          item => !item.includes(templateType) && item !== "all"
        );

        let postcardTypes;

        if (type === "size" && templateType === "all" && data.length === 0) {
          postcardTypes = [];
        } else {
          postcardTypes =
            type === "name"
              ? this.filteredSizes.filter(item => item !== "all")
              : [...postcardTypesSlice, ...data];
        }

        this.fetchMailPiecesDetailedCount({
          template_names: this._getNamesString(templateNames),
          postcard_types: this._getSizesString(postcardTypes),
          start_date: start,
          end_date: end
        });
      }

      this.setTemplateFilter({
        value: data,
        filterType: type,
        templateType
      });
    },

    _getSizesString(array) {
      if (!array) {
        return [];
      }

      return array.filter(item => item !== "all").map(item => item.toLowerCase());
    },

    _quickFilterHandler(e) {
      let startDate = moment();
      let endDate = moment();

      const filterType = e.target.innerText;

      switch (filterType) {
        case "1W":
          startDate = startDate.subtract(6, "d");
          break;

        case "2W":
          startDate = startDate.subtract(13, "d");
          break;

        case "1M":
          startDate = startDate.subtract(1, "month");
          break;

        case "3M":
          startDate = startDate.subtract(3, "month");
          break;

        case "1Y":
          startDate = startDate.subtract(1, "year");
          break;

        case "MTD":
          startDate = startDate.startOf("month");
          break;

        case "QTD":
          startDate = startDate.startOf("quarter");
          break;

        case "YTD":
          startDate = startDate.startOf("year");
          break;

        default:
          return;
      }

      startDate = this._formatDate(startDate, true);
      endDate = this._formatDate(endDate, true);

      this.setQuickFilter(filterType);

      this.fetchMailPiecesDetailedCount({
        template_names: this._getNamesString(this.filteredNames),
        postcard_types: this._getSizesString([
          ...this.filteredPostcardSizes,
          ...this.filteredLetterSizes
        ]),
        start_date: startDate,
        end_date: endDate
      });
    },

    _templateNamesFromIds(array) {
      if (!array) {
        return [];
      }

      let arrToProceed = array;

      if (array.includes("all")) {
        let [all, ...other] = array;
        arrToProceed = other;
      }

      const mapKeyedTemplates = mapKeys(this.templates, "id");
      const mapKeyedLettersTemplates = mapKeys(this.lettersTemplatesList, "id");

      const mapKeyed = {
        ...mapKeyedTemplates,
        ...mapKeyedLettersTemplates
      };

      return arrToProceed.map(item => mapKeyed[item].name);
    },

    _getNamesString(array) {
      return this._templateNamesFromIds(array);
    },

    _formatDate(date, year = false) {
      return moment(date)
        .utc()
        .format(`MMM D${year ? "[,] YYYY" : ""}`);
    },

    /** Convert from letter template key to human readable format
     *  e.g. `usps_first_class-B&W` to `B&W Letter 1st Class`
     *
     * @param item
     **/
    formatLetterTemplate(item) {
      const itemLowered = item.toLowerCase();
      const colored = itemLowered.includes("color");
      const standard = itemLowered.includes("standard");

      return `${colored ? "Color" : "B&W"} Letter ${
        standard ? "Standard" : "1st Class"
      }`;
    },

    closeTemplateFilters(e) {
      if (
        (!this.filtersOpened &&
          !this.datesRangeOpened &&
          !this.templateFiltersOpened) ||
        e.target === this.$refs.filterIconToggle
      ) {
        return;
      }

      const {
        templatesFilters: filterElement,
        filtersOverlay: overlayElement
      } = this.$refs;
      const target = e.target;

      if (
        filterElement !== target &&
        !filterElement.contains(target) &&
        overlayElement !== target
      ) {
        this.filtersOpened = false;
        this.datesRangeOpened = false;
        this.templateFiltersOpened = false;
      }
    },

    onDateChange(values) {
      const { start, end } = values;
      this.setDates({ start, end });
      this.fetchMailPiecesDetailedCount({
        template_names: this._getNamesString(this.filteredNames),
        postcard_types: this._getSizesString([
          ...this.filteredPostcardSizes,
          ...this.filteredLetterSizes
        ]),
        start_date: this._formatDate(start, true),
        end_date: this._formatDate(end, true)
      });
    },

    getMonth(date) {
      return moment(date)
        .startOf("month")
        .format("MMMM");
    },

    ...mapActions("templates", ["fetchTemplatesList"]),
    ...mapActions("lettersTemplates", ["fetchLettersTemplatesList"]),

    ...mapActions("dashboard", [
      "fetchMailPiecesDetailedCount",
      "fetchMailPiecesTotal",
      "setDates",
      "setTemplateFilter",
      "setQuickFilter",
      "filterTemplates"
    ])
  },

  computed: {
    _totalCostString() {
      if (!this.total_cost) {
        return 0;
      }

      return Number(this.total_cost).toLocaleString("en-US", {
        minimumFractionDigits: 2
      });
    },

    _selectedFilterTypesString() {
      if (this.filteredPostcardSizes && this.filteredLetterSizes) {
        if (
          !this.filteredPostcardSizes.length &&
          !this.filteredLetterSizes.length
        ) {
          return "No filtered types";
        }
      }

      return !this.filteredPostcardSizes ||
        !this.filteredLetterSizes ||
        (this.filteredPostcardSizes &&
          this.filteredPostcardSizes.includes("all"))
        ? "All Templates"
        : [...this.filteredPostcardSizes, ...this.filteredLetterSizes]
            .filter(item => item !== "all")
            .map(item =>
              item.includes("postcard")
                ? convertFullTypeKeyToString(item)
                : this.formatLetterTemplate(item)
            )
            .join(", ");
    },

    /**
     * Determine how many pixels should be to the left point applied.
     *
     * Needs to be calculated because AMCharts makes random padding
     * depends on min-max Y axis values.
     * @returns {*}
     * @private
     */
    _leftCheckpointMargin() {
      const maxValueLength = this.maxChartTotalValue.toString().length;

      if (this.maxChartTotalValue < 10) {
        return -4;
      }

      switch (maxValueLength) {
        case 1:
        // return this.maxChartTotalValue >= 5 ? -4 : -4;
        // break
        case 2:
          return this.maxChartTotalValue >= 50 ? 4 : 2;
          break;

        case 3:
          return this.maxChartTotalValue >= 100 ? 12 : 4;
          break;

        case 4:
          return this.maxChartTotalValue >= 1000 ? 22 : 12;
          break;

        case 5:
          return this.maxChartTotalValue >= 10000 ? 30 : 22;
          break;

        case 6:
          return this.maxChartTotalValue >= 100000 ? 38 : 30;
          break;

        default:
          return null;
      }
    },

    /**
     * Current representation of the filter icon state
     * @returns {*}
     * @private
     */
    _filterIcon() {
      return this.filtersOpened || this.filtersHovered
        ? filterIconActive
        : filterIcon;
    },

    selectedDate() {
      return {
        start: this._formatDate(this.start_date, true) || new Date(),
        end: this._formatDate(this.end_date, true) || new Date()
      };
    },

    ...mapGetters("lettersTemplates", {
      lettersTemplatesList: "templatesList"
    }),
    ...mapGetters("templates", ["templates"]),
    ...mapGetters("account", ["getUser"]),
    ...mapGetters("dashboard", [
      "isLoading",
      "isLoadingInitial",
      "items",
      "total",
      "total_cost",
      "start_date",
      "end_date",
      "filteredPostcardSizes",
      "filteredLetterSizes",
      "filteredSizes",
      "filteredNames",
      "quickFilter",
      "filteredTemplates"
    ])
  },

  mounted() {
    if (!this.isLoading) {
      const start_date =
        this.start_date ||
        moment([])
          .subtract(6, "d")
          .format("YYYY-MM-DD");

      const end_date = this.end_date || moment([]).format("YYYY-MM-DD");

      this.fetchMailPiecesDetailedCount({
        start_date,
        end_date,
        postcard_types: [
          "postcard-4x6-usps-standard",
          "postcard-4x6-usps-first-class",
          "postcard-6x9-usps-first-class",
          "postcard-6x9-usps-standard",
          "postcard-6x11-usps-first-class",
          "postcard-6x11-usps-standard",
          "letter-b&w-usps-first-class",
          "letter-b&w-usps-standard",
          "letter-color-usps-standard",
          "letter-color-usps-first-class"
        ],
        initial: true
      });
    }

    this.fetchTemplatesList({ page_size: 100 });
    this.fetchLettersTemplatesList({ page_size: 100 });
    this.filterTemplates();
    document.body.addEventListener("click", this.closeTemplateFilters, true);
  },

  destroyed() {
    document.body.removeEventListener("click", this.closeTemplateFilters, true);
  },

  watch: {
    templates: function(value) {
      this.setTemplateFilter({
        value: value.filter(item => item !== "all").map(item => item.id),
        filterType: "name",
        templateType: "postcard"
      });

      this.setTemplateFilter({
        value: [
          ...new Set([
            "all",
            ...value
              .filter(item => item !== "all")
              .map(item => `${item.type}-${convertMailType(item.mail_type)}`)
          ])
        ],
        filterType: "size",
        templateType: "postcard"
      });
    },

    lettersTemplatesList: function(value) {
      this.setTemplateFilter({
        value: value.filter(item => item !== "all").map(item => item.id),
        filterType: "name",
        templateType: "letter"
      });

      this.setTemplateFilter({
        value: [
          ...new Set([
            "all",
            ...value
              .filter(item => item !== "all")
              .map(
                item =>
                  `letter-${getLetterColor(item.color)}-${convertMailType(
                    item.mail_type
                  )}`
                // item => `${item.color ? "color" : "b&w"}-letter`
              )
          ])
        ],
        filterType: "size",
        templateType: "letter"
      });
    }
  }
});
</script>

<style lang="scss">
$ns: "dashboard-mail";
.#{$ns} {
  &-loader {
    min-height: calc(100vh - 579px);

    &-initial.with-loader__wrapper {
      min-height: calc(100vh - 110px);
    }
  }

  .light {
    font-family: "Lato-Light", Arial, sans-serif;
  }

  .regular {
    font-family: "Lato-Regular", Arial, sans-serif;
  }

  &-loader {
    display: flex;
    justify-content: center;
    flex-direction: column;
  }

  &__no-data {
    margin-top: 20px;
    font-size: 16px;
  }

  &__chart-wrapper {
    position: relative;
    margin-top: 88px;
    width: 100%;
    height: 100%;

    .checkpoints {
      position: absolute;
      width: 100%;
      display: flex;
      justify-content: space-between;
      color: $primary-border-color;
      font-size: 14px;
      height: 100%;
      left: 50px;
      bottom: 16px;
    }

    .checkpoint {
      &.right {
        display: flex;
        margin-right: 80px;

        .dots {
          height: calc(100% - 46px);
          margin-top: 25px;
        }
      }

      .dots {
        min-height: calc(100% - 48px);
        border: 1px dashed #c5c5c5;
        transform: rotate(0);
        width: 1px;
        margin-top: 9px;
      }
    }
  }

  .info-blocks-wrapper {
    margin-top: $general-padding;
    display: flex;
    justify-content: space-between;

    .info-block {
      box-shadow: 2px 4px 25px rgba(0, 0, 0, 0.1);
      border-radius: 5px;
      min-height: 177px;
      flex-basis: 32%;
      padding: 30px;
      line-height: 1.2em;
      background: #fff;

      .top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 20px;

        .item {
          margin-right: 34px;

          &:last-child {
            margin-right: 0;
          }

          .label,
          .value {
            font-size: 12px;
            display: flex;
            align-items: center;
            color: #979797;
          }

          .value {
            color: #4e4e4e;
            font-family: Lato-Bold, sans-serif;
          }
        }
      }

      .bottom {
        .bot-info {
          color: #4e4e4e;
          font-family: Lato-Light, sans-serif;
          font-size: 14px;
        }

        .bot-value {
          margin-top: 10px;
          color: $primary-color;
          font-size: 36px;
          line-height: 1.2em;
        }
      }
    }
  }

  &__content {
    height: 100%;
    position: relative;
    width: 100%;
    padding-top: 30px;
    color: $primary-color;
    line-height: 1.3;
    box-shadow: 2px 4px 25px rgba(0, 0, 0, 0.1);
    border-radius: 5px;

    &.isLoading {
      /*min-height: calc(100vh - 279px);*/
      min-height: 100vh;

      .dashboard-mail__chart-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;

        @media screen and (max-width: 767px) {
          min-height: 500px;
        }
      }
    }

    .filters-overlay {
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.5);
      position: absolute;
      top: 0;
      left: 0;
      z-index: 9999;
    }

    .quick-filters {
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;

      > span {
        display: inline-block;
        cursor: pointer;
        color: $secondary-border-color;
        margin-right: 15px;
        font-size: 14px;
        font-family: Lato-Regular, sans-serif;

        &:last-child {
          margin-right: 0;
        }

        &.active,
        &:hover {
          color: $primary-color;
        }
      }
    }

    .filters-wrapper {
      display: flex;
      justify-content: flex-end;
      position: absolute;
      top: 80px;
      z-index: 1;
      align-items: center;
      width: 100%;
      padding-bottom: 20px;

      .c-pane-container {
        margin-right: 30px;
      }

      .template-filters-wrapper {
        display: flex;
        max-width: 850px;
        justify-content: flex-end;

        @media screen and (max-width: 768px) {
          flex-direction: column;
          align-items: center;
          background: $white;
          box-shadow: 3px 3px 25px rgba(0, 0, 0, 0.25);
          border-radius: 5px;

          /deep/ .v-templates-filter__wrapper .selects {
            flex-direction: column;
            width: 100%;
            height: calc(100% - 60px);

            .select-item {
              height: 50%;
              margin-top: 20px;
              width: 100%;

              &:first-child {
                margin-top: 0;
              }

              .v-checkbox-select__wrapper.items-wrapper {
                height: calc(100% - 25px);
              }
            }
          }

          /deep/ .c-pane-container {
            box-shadow: none !important;
          }
        }
      }

      .type-filters {
        flex: 1;
        margin-right: 30px;
        width: 480px;
        min-width: 55%;

        @media screen and (max-width: 768px) {
          margin: 30px 0;
          width: 100%;

          /deep/ .v-templates-filter__wrapper {
            padding: 0 34px;
            /*height: 100%;*/
            margin: 0;
            box-shadow: none;
          }
        }
      }

      @media screen and (max-width: 768px) {
        top: 0;
        justify-content: center;
      }
    }

    .chart-heading {
      margin-bottom: 20px;
    }

    .top-line {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 0 30px;

      .filters {
        display: flex;
        align-items: flex-start;
        font-size: 14px;
        color: $secondary-border-color;
        cursor: pointer;
        font-family: Lato-Regular, sans-serif;

        .select-filters img,
        .date-range,
        .template-filters {
          margin-right: 30px;
        }

        .date-range,
        .template-filters {
          &.active,
          &:hover {
            color: $primary-color;
          }
        }

        .template-filters {
          max-width: 250px;
          @include textCropEllipsis();
        }

        .date-range {
          display: flex;
          align-items: center;

          img {
            margin: 0 10px;
          }
        }

        .select-filters {
          display: flex;
          align-items: flex-start;

          img {
            &:last-child {
              margin-right: 0;
            }
          }
        }
      }
    }
  }

  .stats {
    display: flex;

    &__item {
      color: $primary-color;
      margin-right: 55px;

      &-main {
        font-size: 26px;
        font-weight: 400;
      }

      &-description {
        font-size: 16px;
        line-height: 1.2;
        color: $secondary-text-color;
        align-items: center;
        margin-bottom: 5px;
        font-family: Lato-Regular, sans-serif;

        span {
          font-family: "Lato-Light", sans-serif;
          font-size: 11px;
          color: #c5c5c5;
        }
      }
    }
  }
}

@media screen and (max-width: 900px) {
  .#{$ns} {
    &__content {
      width: 100%;
    }
  }
}

@media screen and (max-width: 768px) {
  .hide-m {
    display: none !important;
  }
}
</style>

<style lang="scss">
.sidebar-layout-heading.dashboard {
  /*margin-bottom: 25px;*/
}

.sidebar-dashboard .sidebar-layout__content-wrapper {
  padding-bottom: 0 !important;
}
</style>
